import PropTypes from 'prop-types'
import { useEffect, useState } from 'react'
import { LoadingBackdrop } from '@components/loader'
import { NavigationBar } from '@components/navigation-bar'
import { BroadcastBar } from '@components/broadcastBar-bar'
import { BillingStatusBar } from '@components/billing/BillingStatusBar'
import { AdasBox } from '@components/wrapper-components'
import { AuthFailureInterceptor } from '../../api/AuthFailureInterceptor'
import { CaradasAppBar } from './CaradasAppBar'
import { useCenterStore } from '@caradasstore/CenterStore'
import { useEntitlementStore } from '@caradasstore/EntitlementStore'
import { useUserStore } from '@caradasstore/UserStore'
import { entitlementUserKey } from '../../data/entitlementUser'
import { useAuthStore } from '@caradasstore/AuthStore'
import { getCalibrationCenterList, getUserList } from '../../api/api'
import { getCachedCalibrationCenters, setCachedCalibrationCenters } from '../../utils/calibrationCenterCache'
import { refreshCenterDetail } from '../../utils/refreshCenterDetail'
import { hasFetchedCenterDetail, markCenterDetailFetched } from '../../utils/centerDetailFetchGuard'
import {
  getCenterListInFlight,
  isCenterListBootstrapped,
  markCenterListBootstrapped,
  setCenterListInFlight
} from '../../utils/centerListBootstrap'
import { Logger } from '../../logger'
import { emailFromAuthToken } from '../../utils/utils'

// CAR-3630: the once-per-browser-session full center-list fetch is guarded by a
// module-scope flag (see utils/centerListBootstrap). PageLayout remounts on every
// authenticated navigation, so the guard prevents a refetch on each page change.
// The guard lives in its own module so logout paths can reset it without importing
// PageLayout (which imports those components — the reverse import would be circular).

// Reset on every page load by virtue of being module state: distinguishes "the user list
// is cached for this ccId from earlier in this session" from "it was restored from a
// previous session", which the persisted userListCcId cannot tell apart.
let userListFetchedThisLoad = false

// The center re-read (CAR-4038): once per page load per center (guard in utils/centerDetailFetchGuard,
// reset on sign-out - CAR-4086), and on tab focus at most once a minute.
let lastFocusRefreshAt = Date.now()
const FOCUS_REFRESH_MS = 60 * 1000

export const PageLayout = ({ title, children, fullWidth = false }) => {
  const [open, setOpen] = useState(true)

  // Lock the document to the viewport while a PageLayout page is mounted so only the inner
  // content box scrolls (see globals.css `.page-layout-locked`). Scoped here — rather than a
  // global CSS rule — so auth/forbidden pages that render outside PageLayout keep normal
  // document scrolling and never get their content clipped.
  useEffect(() => {
    document.documentElement.classList.add('page-layout-locked')
    return () => document.documentElement.classList.remove('page-layout-locked')
  }, [])

  // Keep CenterStore.userList in sync with the active calibration center on every
  // authenticated page (not just /). Previously the fetch lived only in HomePage,
  // so deep-linking to /quotes/{ref}/summary or switching centers via the global
  // picker while on a non-Home page left the list empty/stale and the assignee
  // dropdown was missing users. Dedupe by tracking which ccId we last fetched for.
  const ccId = useCenterStore((s) => s.ccId)
  const userListCcId = useCenterStore((s) => s.userListCcId)
  // The center's feature entitlements gate the nav and routes (CAR-3710). They are the plan
  // narrowed by the user's role, so they reload per center AND per user; the store dedupes,
  // so this is one call per page load, center switch or sign-in.
  const loadEntitlements = useEntitlementStore((s) => s.loadEntitlements)
  const entitlementUser = useUserStore((s) => entitlementUserKey(s.currentUser))
  useEffect(() => {
    if (ccId) loadEntitlements(ccId)
  }, [ccId, entitlementUser, loadEntitlements])

  // The center's admin switches (AI Report, Scheduler) live on the persisted centerDetail, so a
  // signed-in user would keep the copy from their last center switch (CAR-4038). Re-read it once
  // per page load, and with the entitlements when the tab comes back after a while.
  useEffect(() => {
    if (!ccId || hasFetchedCenterDetail(ccId)) return
    markCenterDetailFetched(ccId)
    refreshCenterDetail(ccId)
  }, [ccId])
  useEffect(() => {
    const onFocus = () => {
      const activeCcId = useCenterStore.getState().ccId
      if (!activeCcId || document.visibilityState !== 'visible' || Date.now() - lastFocusRefreshAt < FOCUS_REFRESH_MS) return
      lastFocusRefreshAt = Date.now()
      refreshCenterDetail(activeCcId)
      useEntitlementStore.getState().loadEntitlements(activeCcId, { force: true })
    }
    window.addEventListener('focus', onFocus)
    document.addEventListener('visibilitychange', onFocus)
    return () => {
      window.removeEventListener('focus', onFocus)
      document.removeEventListener('visibilitychange', onFocus)
    }
  }, [])
  const setUserList = useCenterStore((s) => s.setUserList)
  const setUserListCcId = useCenterStore((s) => s.setUserListCcId)
  const userEmail = useUserStore((s) => s.userEmail)
  const setCurrentUser = useUserStore((s) => s.setCurrentUser)
  const upsertCenterSummaries = useCenterStore((s) => s.upsertCenterSummaries)
  const setCenterListLoaded = useCenterStore((s) => s.setCenterListLoaded)
  // GET /v1/calibrationcenters is admin-only; non-admins 403 (CAR-3670). Gate the
  // bootstrap on the same admin signal CalibrationCenterBar uses.
  const isCenterAdminFlag = useCenterStore((s) => s.isCenterAdmin)
  const isSystemAdmin = useUserStore((s) => !!s.currentUser?.is_admin)
  const isAdmin = isCenterAdminFlag || isSystemAdmin

  // CAR-3630: load the full calibration-center list at authenticated bootstrap so
  // the navbar location switcher shows every location on first paint — instead of
  // relying on the hover-mounted CalibrationCenterBar, which often missed the fetch.
  // Hydrate instantly from the session cache, then fetch the authoritative list
  // with retry (a swallowed/failed fetch previously left only the current center).
  // The switcher stays disabled (centerListLoaded=false) until either a warm cache
  // (a prior successful fetch, always the full list) or the bootstrap API confirms.
  useEffect(() => {
    const cached = getCachedCalibrationCenters()
    if (cached.length > 0) upsertCenterSummaries(cached)

    // Non-admins must not hit the admin-only list endpoint (403 → /forbidden). Their
    // switcher is backfilled per-ID from accessibleCcIds by CalibrationCenterBar, so
    // mark the list ready and skip the bootstrap fetch entirely.
    if (!isAdmin) {
      setCenterListLoaded(true)
      return
    }

    setCenterListLoaded(cached.length > 0 || isCenterListBootstrapped())

    if (isCenterListBootstrapped() || getCenterListInFlight()) return

    const fetchFullList = async () => {
      // First page returns totalCount; refetch with the full size when there are
      // more rows than the probe returned (mirrors Manage Locations / CAR-3522).
      // customErrorHandling: a stray 403 (e.g. misclassified role) surfaces to the
      // retry loop instead of tripping the interceptor's redirect to /forbidden.
      const PROBE_PAGE_SIZE = 500
      const initial = await getCalibrationCenterList({ pageNo: 0, pageSize: PROBE_PAGE_SIZE, customErrorHandling: true })
      if (initial?.status !== 200) return null
      const total = initial.data?.totalCount || 0
      let centers = initial.data?.calibrationCenters || []
      if (total > centers.length) {
        const full = await getCalibrationCenterList({ pageNo: 0, pageSize: total, customErrorHandling: true })
        if (full?.status === 200) centers = full.data?.calibrationCenters || centers
      }
      return centers
    }

    const loadWithRetry = async () => {
      const MAX_ATTEMPTS = 3
      for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
        try {
          const centers = await fetchFullList()
          if (centers) {
            upsertCenterSummaries(centers)
            setCachedCalibrationCenters(centers)
            markCenterListBootstrapped()
            setCenterListLoaded(true)
            return
          }
        } catch (err) {
          Logger.error({ message: err, payload: { file: 'PageLayout', method: 'bootstrapCenterList', attempt } })
        }
        if (attempt < MAX_ATTEMPTS) {
          await new Promise((resolve) => setTimeout(resolve, 600 * attempt))
        }
      }
    }

    setCenterListInFlight(
      loadWithRetry().finally(() => {
        setCenterListInFlight(null)
      })
    )
  }, [upsertCenterSummaries, setCenterListLoaded, isAdmin])

  useEffect(() => {
    // userListCcId is persisted, so on its own it suppresses this fetch for the whole
    // life of the browser profile - the app would never re-read the signed-in user's
    // roles, and a role changed server-side would not take effect until the next login.
    // That is how a client user kept every edit control: the persisted currentUser
    // predated the role and nothing refreshed it. Fetch once per page load, then let
    // the ccId dedupe take over for in-session navigation.
    if (!ccId || (userListCcId === ccId && userListFetchedThisLoad)) return
    let cancelled = false
    const run = async () => {
      try {
        const res = await getUserList({ ccid: ccId })
        if (cancelled || res?.status !== 200) return
        userListFetchedThisLoad = true
        setUserList(res.data || [])
        setUserListCcId(ccId)
        // The token is the authoritative identity; userEmail is a persisted convenience
        // that can be empty on a restored session, and guarding solely on it meant
        // currentUser - and therefore the user's role - was never refreshed.
        const signedInEmail = userEmail || emailFromAuthToken(useAuthStore.getState().token)
        if (signedInEmail) {
          const me = res.data?.find((u) => u.email?.toLowerCase() === signedInEmail.toLowerCase())
          if (me) setCurrentUser(me)
        }
      } catch (err) {
        Logger.error({ message: err, payload: { file: 'PageLayout', method: 'fetchUserList', ccId } })
      }
    }
    run()
    return () => {
      cancelled = true
    }
  }, [ccId, userListCcId, setUserList, setUserListCcId, setCurrentUser, userEmail])

  return (
    <AdasBox className='flex-col min-[435px]:flex-row flex h-[100dvh] overflow-hidden'>
      {/* To Provide Gap Only for mobile */}
      <LoadingBackdrop />
      <NavigationBar onClick={() => setOpen(!open)} className='h-[100dvh]' title={title} />
      <AuthFailureInterceptor />
      {/* Sole scroller for page content. flex-1 + min-h-0 lets it fill the space left by the
          sidebar (desktop) or the mobile top bar and scroll internally, instead of a rigid
          h-[100dvh] that overflowed the shell and let the whole document scroll (CAR white-screen). */}
      <AdasBox className='overflow-auto flex-1 min-h-0 min-w-0 bg-background'>
        <AdasBox className='hidden min-[435px]:block'>
          <CaradasAppBar title={title} />
        </AdasBox>
        <BroadcastBar />
        <BillingStatusBar />
        {/* Standard content wrapper — centres the page inside a 1400px max-width column
            so summary pages don't stretch on ultra-wide displays. Pages that genuinely
            need the full viewport (e.g. Scheduler calendar) opt out via
            layoutProps.fullWidth. */}
        <AdasBox className={`px-3 min-[435px]:px-6 ${fullWidth ? '' : 'mx-auto w-full max-w-[1400px]'}`}>{children}</AdasBox>
      </AdasBox>
    </AdasBox>
  )
}

PageLayout.propTypes = {
  title: PropTypes.string,
  children: PropTypes.node,
  fullWidth: PropTypes.bool
}
